import React, { useMemo, useRef } from 'react'

type OtpInputProps = {
    value: string
    onChange: (value: string) => void
    onBlur?: () => void
    length?: number
    disabled?: boolean
    className?: string
    inputClassName?: string
    id?: string
    onComplete?: () => void
}

// keep the input clean of non-digit characters
const sanitizeDigits = (value: string) => value.replace(/\D/g, '')

const OtpInput: React.FunctionComponent<OtpInputProps> = ({
    value,
    onChange,
    onBlur,
    length = 6,
    disabled = false,
    className = '',
    inputClassName = '',
    id,
    onComplete
}: OtpInputProps) => {
    const normalizedValue = useMemo(() => sanitizeDigits(value).slice(0, length), [value, length])
    const digits = useMemo(
        () => Array.from({ length }, (_, index) => normalizedValue[index] ?? ''),
        [normalizedValue, length]
    )
    const inputRefs = useRef<Array<HTMLInputElement | null>>([])

    const focusIndex = (index: number) => {
        const clampedIndex = Math.max(0, Math.min(length - 1, index))
        inputRefs.current[clampedIndex]?.focus()
        inputRefs.current[clampedIndex]?.select()
    }

    const updateDigits = (nextDigits: string[]) => {
        onChange(nextDigits.join(''))
    }

    const applyDigits = (startIndex: number, incomingDigits: string) => {
        const nextDigits = [...digits]
        let cursor = startIndex

        for (const digit of incomingDigits) {
            if (cursor >= length) break
            nextDigits[cursor] = digit
            cursor += 1
        }

        updateDigits(nextDigits)
        focusIndex(cursor < length ? cursor : length - 1)
    }

    const handleChange = (index: number, nextValue: string) => {
        const incomingDigits = sanitizeDigits(nextValue)

        if (incomingDigits.length === 0) {
            const nextDigits = [...digits]
            nextDigits[index] = ''
            updateDigits(nextDigits)
            return
        }

        if (incomingDigits.length === 1) {
            const nextDigits = [...digits]
            nextDigits[index] = incomingDigits
            updateDigits(nextDigits)
            // Check if OTP is now complete
            if (onComplete && nextDigits.every((d) => d !== '') && nextDigits.length === length) {
                setTimeout(onComplete, 0)
            }
            if (index < length - 1) {
                focusIndex(index + 1)
            }
            return
        }

        applyDigits(index, incomingDigits)
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (event.key === 'Backspace') {
            event.preventDefault()
            const nextDigits = [...digits]

            if (nextDigits[index]) {
                nextDigits[index] = ''
                updateDigits(nextDigits)
                return
            }

            if (index > 0) {
                nextDigits[index - 1] = ''
                updateDigits(nextDigits)
                focusIndex(index - 1)
            }
            return
        }

        if (event.key === 'Delete') {
            event.preventDefault()
            const nextDigits = [...digits]
            nextDigits[index] = ''
            updateDigits(nextDigits)
            return
        }

        if (event.key === 'ArrowLeft') {
            event.preventDefault()
            focusIndex(index - 1)
            return
        }

        if (event.key === 'ArrowRight') {
            event.preventDefault()
            focusIndex(index + 1)
            return
        }
    }

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault()
        const pasted = sanitizeDigits(event.clipboardData.getData('text'))
        if (!pasted) {
            return
        }
        applyDigits(0, pasted)
        // if a complete OTP was pasted, call onComplete after state propagates
        if (onComplete && pasted.length >= length) {
            setTimeout(onComplete, 0)
        }
    }

    return (
        <div className='w-full mt-4 mb-2 space-y-2'>
            <div className={`flex items-center gap-2 justify-center flex-wrap ${className}`}>
                {digits.map((digit, index) => {
                    const currentId = id ? `${id}-${index}` : undefined
                    return (
                        <input
                            key={index}
                            ref={(element) => {
                                inputRefs.current[index] = element
                            }}
                            id={currentId}
                            type='text'
                            inputMode='numeric'
                            pattern='[0-9]*'
                            autoComplete={index === 0 ? 'one-time-code' : 'off'}
                            maxLength={1}
                            value={digit}
                            disabled={disabled}
                            onBlur={onBlur}
                            onFocus={(event) => {
                                event.currentTarget.select()
                            }}
                            onChange={(event) => handleChange(index, event.target.value)}
                            onKeyDown={(event) => handleKeyDown(event, index)}
                            onPaste={handlePaste}
                            className={`h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 rounded border border-gray-300 text-center text-xl font-semibold focus:border-[--tb-primary] focus:outline-none focus:ring-1 focus:ring-[--tb-primary] focus:ring-opacity-20 disabled:opacity-60 ${inputClassName}`}
                            aria-label={`Verificatiecode cijfer ${index + 1}`}
                        />
                    )
                })}
            </div>
            <label className='text-xs font-normal text-[--tb-textLight] mt-1 block text-center'>
                Voer de {length}-cijferige verificatiecode uit de e-mail in
            </label>
        </div>
    )
}

export default OtpInput
